import { getUserAgentData } from "./browser-os";

export type MacArchitecture = "apple-silicon" | "intel";

/** `undefined` = not a Mac; `"unknown"` = a Mac we can't classify. */
export type MacArchResult = MacArchitecture | "unknown" | undefined;

export interface MacArchSignals {
  isMac: boolean;
  clientHintArchitecture: string | undefined;
  webglRenderer: string | undefined;
  webglExtensions: readonly string[] | undefined;
}

const APPLE_SILICON_RENDERER = /Apple M\d/i;
const SOFTWARE_RENDERER = /SwiftShader|llvmpipe|Software/i;
const APPLE_SILICON_TEXTURE_EXTENSIONS = [
  "WEBGL_compressed_texture_astc",
  "WEBGL_compressed_texture_etc",
];

/**
 * Client Hints "x86" is ignored (Rosetta reports x86 on Apple Silicon).
 * Texture extensions reflect real GPU hardware, surviving Rosetta and masking.
 */
export function resolveMacArchitecture({
  isMac,
  clientHintArchitecture,
  webglRenderer,
  webglExtensions,
}: MacArchSignals): MacArchResult {
  if (!isMac) {
    return undefined;
  }
  if (clientHintArchitecture === "arm") {
    return "apple-silicon";
  }
  if (webglRenderer && APPLE_SILICON_RENDERER.test(webglRenderer)) {
    return "apple-silicon";
  }
  if (webglRenderer && SOFTWARE_RENDERER.test(webglRenderer)) {
    return "unknown";
  }
  if (!webglExtensions) {
    return "unknown";
  }
  if (
    APPLE_SILICON_TEXTURE_EXTENSIONS.some((ext) =>
      webglExtensions.includes(ext),
    )
  ) {
    return "apple-silicon";
  }
  return "intel";
}

async function getClientHintArchitecture(): Promise<string | undefined> {
  try {
    const uaData = getUserAgentData();
    if (!uaData) {
      return undefined;
    }
    const hints = await uaData.getHighEntropyValues(["architecture"]);
    return hints.architecture;
  } catch (error) {
    console.warn("Failed to read Client Hints architecture", error);
    return undefined;
  }
}

function getWebglSignals(): Pick<
  MacArchSignals,
  "webglRenderer" | "webglExtensions"
> {
  const gl = document.createElement("canvas").getContext("webgl");
  if (!gl) {
    return { webglRenderer: undefined, webglExtensions: undefined };
  }
  const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
  const renderer: unknown = debugInfo
    ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
    : gl.getParameter(gl.RENDERER);
  const webglExtensions = gl.getSupportedExtensions() ?? undefined;
  gl.getExtension("WEBGL_lose_context")?.loseContext();
  return {
    webglRenderer: typeof renderer === "string" ? renderer : undefined,
    webglExtensions,
  };
}

function isMacUserAgent(): boolean {
  const ua = navigator.userAgent;
  return (
    /Macintosh/.test(ua) &&
    !/iPhone|iPad|iPod/.test(ua) &&
    navigator.maxTouchPoints === 0
  );
}

async function detectMacArchitecture(): Promise<MacArchResult> {
  const isMac = isMacUserAgent();
  if (!isMac) {
    return undefined;
  }
  try {
    const webgl = getWebglSignals();
    const clientHintArchitecture = await getClientHintArchitecture();
    return resolveMacArchitecture({ isMac, clientHintArchitecture, ...webgl });
  } catch (error) {
    console.warn("Failed to detect Mac architecture", error);
    return "unknown";
  }
}

let macArchitecturePromise: Promise<MacArchResult> | undefined;

export function getMacArchitecture(): Promise<MacArchResult> {
  macArchitecturePromise ??= detectMacArchitecture();
  return macArchitecturePromise;
}
